import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { SupergraphListGraphDeferredMetadataFragment } from 'src/app/account/supergraphListPage/SupergraphsListItem';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ServiceAlreadyExistsRegex } from 'src/lib/graphqlTypes/serverErrors';
import { OnboardingArchitecture } from 'src/lib/graphqlTypes/types';
export type PublishArch = 'federation' | 'monolith';

interface CreateGraphConfig {
  accountId: string;
  graphId: string;
  graphTitle: string;
  publishArch?: PublishArch | string | undefined;
}

export const useCreateGraph = () => {
  const client = useApolloClient();

  const [addGraph, { error: addGraphError }] = useMutation<
    GraphQLTypes.AddGraphMutation,
    GraphQLTypes.AddGraphMutationVariables
  >(
    gql`
      mutation AddGraphMutation(
        $accountId: ID!
        $graphId: ID!
        $graphTitle: String!
        $hiddenFromUninvitedNonAdminAccountMembers: Boolean!
        $onboardingArchitecture: OnboardingArchitecture!
      ) {
        newService(
          id: $graphId
          title: $graphTitle
          accountId: $accountId
          hiddenFromUninvitedNonAdminAccountMembers: $hiddenFromUninvitedNonAdminAccountMembers
          onboardingArchitecture: $onboardingArchitecture
        ) {
          id
          title
          account {
            id
          }
          ...SupergraphListGraphDeferredMetadataFragment
        }
      }
      ${SupergraphListGraphDeferredMetadataFragment}
    `,
    {
      context: appLinkContext({
        ignoreSentryReporting: [
          (graphqlError) =>
            ServiceAlreadyExistsRegex.test(graphqlError.message),
        ],
      }),
      update(cache, result, { variables }) {
        const successfulResult = result.data?.newService;
        if (!successfulResult) return;

        cache.modify({
          id: cache.identify({
            __typename: 'Account',
            id: variables?.accountId,
          }),
          fields: {
            services: (cachedServices = [], { toReference }) => {
              const newServiceRef = toReference({
                __typename: successfulResult.__typename,
                id: successfulResult.id,
              });
              return cachedServices.concat(newServiceRef);
            },
            graphsConnection: (
              cachedGraphsConnection = {},
              { toReference },
            ) => {
              const newServiceRef = toReference({
                __typename: successfulResult.__typename,
                id: successfulResult.id,
              });
              return {
                ...cachedGraphsConnection,
                edges: [
                  ...cachedGraphsConnection.edges,
                  {
                    __typename: 'AccountGraphEdge',
                    node: newServiceRef,
                  },
                ],
              };
            },
          },
        });
      },
    },
  );

  const createGraph = useCallback(
    async ({
      accountId,
      graphId,
      graphTitle,
      hiddenFromUninvitedNonAdminAccountMembers,
      publishArch,
    }: CreateGraphConfig & {
      hiddenFromUninvitedNonAdminAccountMembers: boolean;
    }) => {
      const addGraphResult = await addGraph({
        variables: {
          accountId,
          graphId,
          graphTitle,
          hiddenFromUninvitedNonAdminAccountMembers,
          onboardingArchitecture:
            publishArch === 'federation'
              ? OnboardingArchitecture.SUPERGRAPH
              : OnboardingArchitecture.MONOLITH,
        },
      });

      if (!addGraphResult.data?.newService) {
        return false;
      }

      client.cache.evict({
        id: client.cache.identify({
          __typename: addGraphResult.data.newService.account?.__typename,
          id: addGraphResult.data.newService.account?.id,
        }),
        fieldName: 'graphs',
      });

      client.cache.gc();

      return true;
    },
    [addGraph, client],
  );

  const createGraphError = addGraphError;

  return { createGraph, createGraphError };
};
